import React, { useEffect, useMemo, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';

import Nav from './nav';
import WhiteLabelForm from '../../components/whiteLabelForm';
import { BASEURL, PRIMARYCOLOR, COMPANY } from '../../Constants';
import { buildDarkwebReportPdfData } from './darkwebReportPdfHelpers';

import './ReportsPage.css';

const DEFAULT_PRIMARY = PRIMARYCOLOR || '#005CC5';
const DEFAULT_COMPANY = COMPANY || 'StealthNet AI';
const DEFAULT_CLIENT = '';

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const ExecutiveReportDarkweb = ({
  minimal = false,
  primary = PRIMARYCOLOR,
  company = COMPANY,
  client,
  reportData = null,
}) => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [reportResponse, setReportResponse] = useState(null);
  const [leaksPayload, setLeaksPayload] = useState(null);
  const [loading, setLoading] = useState(!reportData);
  const [downloading, setDownloading] = useState(false);

  const [primaryColor, setPrimaryColor] = useState(primary || DEFAULT_PRIMARY);
  const [companyName, setCompanyName] = useState(company || DEFAULT_COMPANY);
  const [clientName, setClientName] = useState(client || DEFAULT_CLIENT);

  const applyWhiteLabel = ({ primary: nextPrimary, company: nextCompany, client: nextClient }) => {
    if (nextPrimary !== undefined) setPrimaryColor(nextPrimary);
    if (nextCompany !== undefined) setCompanyName(nextCompany);
    if (nextClient !== undefined) setClientName(nextClient);
  };

  useEffect(() => {
    setPrimaryColor(primary || DEFAULT_PRIMARY);
  }, [primary]);

  useEffect(() => {
    setCompanyName(company || DEFAULT_COMPANY);
  }, [company]);

  useEffect(() => {
    if (client !== undefined) {
      setClientName(client);
    }
  }, [client]);

  useEffect(() => {
    if (reportData) {
      setLoading(false);
      return;
    }

    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${BASEURL}/pentests/darkweb/report?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
      fetch(`${BASEURL}/pentests/darkweb/leaks?pentest_id=${pentest_id}`, { headers }).then((response) => response.json()),
    ])
      .then(([reportRes, leaksRes]) => {
        if ([reportRes, leaksRes].some((result) => result?.message === 'Invalid token')) {
          navigate('/');
          return;
        }

        setReportResponse(reportRes || null);
        setLeaksPayload(leaksRes || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate, pentest_id, reportData]);

  const report = useMemo(() => {
    if (reportData) return reportData;
    if (!reportResponse && !leaksPayload) return null;

    return buildDarkwebReportPdfData({
      pentestId: pentest_id,
      primaryColor,
      companyName,
      clientName,
      reportResponse,
      leaksPayload,
    });
  }, [clientName, companyName, leaksPayload, pentest_id, primaryColor, reportData, reportResponse]);

  const handleDownloadPDF = async () => {
    if (!report) return;

    setDownloading(true);
    try {
      const [{ pdf }, { DarkwebExecutivePdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('./darkwebReportPdf'),
      ]);

      const blob = await pdf(<DarkwebExecutivePdfDocument report={report} />).toBlob();
      downloadBlob(blob, `Executive_Report_Darkweb_${pentest_id}.pdf`);
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !report) {
    return (
      <Box sx={{ display: 'flex', height: minimal ? 'auto' : '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (minimal) {
    return (
      <Box className="report-surface" sx={{ maxWidth: '8.5in', mx: 'auto', py: 4, px: 3 }}>
        <ExecutiveBody report={report} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <Nav />
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          sx={{
            position: 'absolute',
            top: 8,
            right: 16,
            backgroundColor: DEFAULT_PRIMARY,
            '&:hover': {
              backgroundColor: darken(DEFAULT_PRIMARY, 0.12),
            },
          }}
        >
          Download PDF
        </Button>
      </Box>

      <Backdrop open={downloading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card className="report-config-card" sx={{ width: '100%', maxWidth: '8.5in', mb: 2, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.08)' }}>
          <WhiteLabelForm
            defaultPrimary={DEFAULT_PRIMARY}
            defaultCompany={DEFAULT_COMPANY}
            defaultClient={DEFAULT_CLIENT}
            onApply={applyWhiteLabel}
            showCompany
            showClient
            title="White Label"
          />
        </Card>
      </Box>

      <Box className="report-platform-shell" sx={{ minHeight: '100vh', pt: 2, pb: 6, px: 2 }}>
        <Box className="pdf-page exec-page" sx={{ display: 'flex', justifyContent: 'center' }} data-report-section="executive">
          <Card className="report-page-card report-page-card--standard exec-wrapper allow-split" sx={{ width: '100%', maxWidth: '8.5in' }}>
            <Box
              sx={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#ffffff',
                px: '0.72in',
                py: '0.68in',
              }}
            >
              <ExecutiveBody report={report} />
            </Box>
          </Card>
        </Box>
      </Box>
    </>
  );
};

const Header = ({ report }) => (
  <Card
    className="avoid-break"
    sx={{
      p: 2.5,
      mb: 2.5,
      boxShadow: 0,
      border: `1px solid ${alpha(report.brand.ink, 0.08)}`,
      bgcolor: '#fff',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 6,
        background: `linear-gradient(90deg, ${report.brand.primaryDark}, ${report.brand.primaryMid} 60%, ${report.brand.primaryLight})`,
      }}
    />

    <Grid container spacing={1.5} alignItems="center">
      <Grid item xs={12} md={8}>
        <Typography id="executive-security-assessment" variant="h5" sx={{ fontWeight: 900, lineHeight: 1.2, color: report.brand.ink }}>
          Executive Security Assessment
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(report.brand.ink, 0.7) }}>
          {report.organizationName} | {report.testingType} | ID #{report.pentestId}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(report.brand.ink, 0.7) }}>
          {report.reportDate}
        </Typography>

        <Stack direction="row" spacing={0.75} sx={{ mt: 0.8, flexWrap: 'wrap' }}>
          {[
            report.companyName,
            'Credential Exposure',
            'Breach Enrichment',
          ].map((label, index) => (
            <Chip
              key={label}
              size="small"
              label={label}
              sx={{
                bgcolor: index === 0 ? alpha(report.brand.primary, 0.08) : index === 1 ? alpha(report.brand.primaryLight, 0.1) : alpha(report.brand.primaryDark, 0.1),
                color: report.brand.primaryMid,
                border: `1px solid ${alpha(report.brand.primary, 0.2)}`,
                fontWeight: 700,
                borderRadius: '999px',
              }}
            />
          ))}
        </Stack>
      </Grid>

      <Grid item xs={12} md={4}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
          <Chip
            label="Monitoring Review"
            sx={{
              mb: 0.8,
              bgcolor: report.brand.primaryMid,
              color: '#fff',
              fontWeight: 800,
              borderRadius: '12px',
            }}
          />
          <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.68) }}>
            Leak records observed: {report.totalLeakRecords}
          </Typography>
          <Typography variant="caption" sx={{ color: alpha(report.brand.ink, 0.68) }}>
            Validated breach datasets: {report.uniqueBreachDbsCount}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  </Card>
);

const MetricCard = ({ report, label, value, hint, accent }) => (
  <Card
    className="avoid-break"
    sx={{
      p: 2,
      borderRadius: 3,
      border: `1px solid ${alpha(accent, 0.18)}`,
      background: alpha(accent, 0.08),
      boxShadow: 'none',
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
    }}
  >
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: accent }} />
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', mb: 0.75 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '1.8rem', fontWeight: 800, color: report.brand.ink, lineHeight: 1, mb: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.86rem', lineHeight: 1.5, color: alpha(report.brand.ink, 0.8) }}>
      {hint}
    </Typography>
  </Card>
);

const RankedListCard = ({ report, title, subtitle, items, accent, valueKey, metaRenderer }) => {
  const maxValue = Math.max(1, ...items.map((item) => item[valueKey] || 0));

  return (
    <Card
      className="avoid-break"
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: `1px solid ${alpha(report.brand.ink, 0.08)}`,
        boxShadow: 'none',
        height: '100%',
      }}
    >
      <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '1rem', fontWeight: 800, color: report.brand.ink, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: alpha(report.brand.ink, 0.68), mb: 1.5 }}>
        {subtitle}
      </Typography>

      <Stack spacing={1.2}>
        {items.length ? items.map((item) => {
          const percent = Math.max(8, Math.round(((item[valueKey] || 0) / maxValue) * 100));
          return (
            <Box key={item.email || item.name}>
              <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.35 }}>
                <Typography sx={{ fontWeight: 700, color: report.brand.ink, overflowWrap: 'anywhere' }}>
                  {item.email || item.name}
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#334155' }}>
                  {item[valueKey] || 0}
                </Typography>
              </Stack>
              <Box sx={{ height: 8, borderRadius: 999, backgroundColor: '#e2e8f0', overflow: 'hidden', mb: 0.35 }}>
                <Box sx={{ width: `${percent}%`, height: '100%', borderRadius: 999, backgroundColor: accent }} />
              </Box>
              {metaRenderer ? (
                <Typography sx={{ fontSize: '0.74rem', color: alpha(report.brand.ink, 0.62) }}>
                  {metaRenderer(item)}
                </Typography>
              ) : null}
            </Box>
          );
        }) : (
          <Typography sx={{ color: alpha(report.brand.ink, 0.64) }}>
            No data available.
          </Typography>
        )}
      </Stack>
    </Card>
  );
};

const ExecutiveBody = ({ report }) => (
  <>
    <Header report={report} />

    <Grid container spacing={1.5} sx={{ mb: 1.8 }}>
      <Grid item xs={12} md={3}>
        <MetricCard report={report} label="Leak Records" value={report.totalLeakRecords} hint="Observed records tied to monitored identities across collected dark web sources." accent={report.brand.primaryMid} />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard report={report} label="Impacted Emails" value={report.uniqueEmailsCount} hint="Unique monitored identities observed across leak records and breach enrichment." accent="#c62828" />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard report={report} label="Breach Databases" value={report.uniqueBreachDbsCount} hint="Distinct breach datasets associated with monitored identities in scope." accent={report.brand.primaryDark} />
      </Grid>
      <Grid item xs={12} md={3}>
        <MetricCard report={report} label="Leak Sources" value={report.uniqueLeakSourceCount} hint="Unique source databases observed before enrichment and correlation." accent={report.brand.primaryLight} />
      </Grid>
    </Grid>

    <Grid container spacing={1.75} sx={{ mb: 1.8 }}>
      <Grid item xs={12} md={6}>
        <RankedListCard
          report={report}
          title="Top Impacted Identities"
          subtitle="Ranked by leaked record count, with breach coverage shown beneath each identity."
          items={report.topImpactedEmails}
          accent={report.brand.primaryMid}
          valueKey="count"
          metaRenderer={(item) => `${item.breachCount || 0} breach dataset(s) linked`}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <RankedListCard
          report={report}
          title="Database Breach Reach"
          subtitle="Validated breach databases ranked by how many monitored identities they affect."
          items={report.topBreachDatabases}
          accent={report.brand.primaryDark}
          valueKey="impactedEmails"
          metaRenderer={(item) => item.dateLabel || 'No breach date available'}
        />
      </Grid>
    </Grid>

    <Card
      className="avoid-break"
      sx={{
        p: 2.25,
        mb: 1.8,
        borderRadius: 3,
        border: `1px solid ${alpha(report.brand.primary, 0.14)}`,
        backgroundColor: alpha(report.brand.primary, 0.04),
        boxShadow: 'none',
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b', mb: 0.6 }}>
        Most Recent Verified Breach
      </Typography>
      <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '1.15rem', fontWeight: 800, color: report.brand.ink, mb: 0.5 }}>
        {report.mostRecentBreach ? `${report.mostRecentBreach.name} | ${report.mostRecentBreachLabel}` : 'No breach dates available'}
      </Typography>
      <Typography sx={{ fontSize: '0.92rem', lineHeight: 1.55, color: alpha(report.brand.ink, 0.78) }}>
        {report.mostRecentBreach
          ? `${report.mostRecentBreach.impactedEmails} monitored identity(ies) were associated with the most recent breach event identified during enrichment.`
          : 'The assessment team did not identify a verified breach date in the enrichment data returned for this engagement.'}
      </Typography>
    </Card>

    <Card
      id="executive-summary"
      className="exec-summary-card allow-split"
      sx={{
        p: 2.5,
        border: `1px solid ${alpha(report.brand.ink, 0.08)}`,
        boxShadow: 0,
        bgcolor: '#fff',
      }}
    >
      <Typography sx={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: '1.2rem', fontWeight: 800, color: report.brand.ink, mb: 0.75 }}>
        Executive Summary
      </Typography>
      <Typography sx={{ fontSize: '0.84rem', lineHeight: 1.5, color: alpha(report.brand.ink, 0.68), mb: 1.25 }}>
        This section summarizes the monitoring results in narrative form for leadership and stakeholder review.
      </Typography>
      <Divider sx={{ mb: 1.5, borderColor: alpha(report.brand.ink, 0.08) }} />
      <Box className="exec-summary">
        <ReactMarkdown className="markdown-content" remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
          {report.executiveSummaryMarkdown || ''}
        </ReactMarkdown>
      </Box>
    </Card>
  </>
);

export default ExecutiveReportDarkweb;
